import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { getAnalyticsSummary, getSearchAnalytics, getViewAnalytics } from '../api/client';
import type { AnalyticsSummary, SearchAnalytics, ViewAnalytics } from '../types';
import styles from './Analytics.module.css';

export function AnalyticsPage() {
    const { clientId } = useParams<{ clientId: string }>();
    const [period, setPeriod] = useState(30);
    const [summary, setSummary] = useState<AnalyticsSummary | null>(null);
    const [searchData, setSearchData] = useState<SearchAnalytics | null>(null);
    const [viewData, setViewData] = useState<ViewAnalytics | null>(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        if (clientId) loadAnalytics();
    }, [clientId, period]);

    const loadAnalytics = async () => {
        if (!clientId) return;
        setLoading(true);
        try {
            const [summaryRes, searchRes, viewRes] = await Promise.all([
                getAnalyticsSummary(clientId, period),
                getSearchAnalytics(clientId, period),
                getViewAnalytics(clientId, period),
            ]);
            setSummary(summaryRes);
            setSearchData(searchRes);
            setViewData(viewRes);
        } catch (err) {
            console.error('Failed to load analytics:', err);
        }
        setLoading(false);
    };

    if (loading) {
        return (
            <div className={styles.loading}>
                <div className="spinner" />
                <p>Loading analytics...</p>
            </div>
        );
    }

    const prefLabels: Record<string, string> = {
        gender: '👤 Gender',
        occasion: '📅 Occasion',
        scent_family: '💐 Scent Family',
        intensity: '💪 Intensity',
        personality: '✨ Personality',
    };

    return (
        <div className={styles.container}>
            <header className={styles.header}>
                <div>
                    <h1>📊 Analytics</h1>
                    <p>Insight dari pencarian dan preferensi pelanggan</p>
                </div>
                <select
                    value={period}
                    onChange={(e) => setPeriod(Number(e.target.value))}
                    className={styles.periodSelect}
                >
                    <option value={7}>7 Hari Terakhir</option>
                    <option value={30}>30 Hari Terakhir</option>
                    <option value={90}>90 Hari Terakhir</option>
                </select>
            </header>

            {/* Summary Stats */}
            <div className={styles.statsGrid}>
                <div className={styles.statCard}>
                    <h3>Total Searches</h3>
                    <div className={styles.statValue}>{summary?.searches?.total_searches || 0}</div>
                </div>
                <div className={styles.statCard}>
                    <h3>AI Searches</h3>
                    <div className={styles.statValue}>{summary?.searches?.ai_searches || 0}</div>
                </div>
                <div className={styles.statCard}>
                    <h3>Quiz</h3>
                    <div className={styles.statValue}>{summary?.searches?.quiz_searches || 0}</div>
                </div>
                <div className={styles.statCard}>
                    <h3>Product Views</h3>
                    <div className={styles.statValue}>{summary?.views?.total_views || 0}</div>
                </div>
            </div>

            {/* Two Column Layout */}
            <div className={styles.twoCol}>
                {/* Top Searches */}
                <div className={styles.card}>
                    <h2>🔍 Top Pencarian AI</h2>
                    {searchData?.topQueries && searchData.topQueries.length > 0 ? (
                        <div className={styles.list}>
                            {searchData.topQueries.map((q, i) => (
                                <div key={i} className={styles.listItem}>
                                    <span>{i + 1}. "{q.query}"</span>
                                    <span className={styles.count}>{q.count}x</span>
                                </div>
                            ))}
                        </div>
                    ) : (
                        <p className={styles.empty}>Belum ada data pencarian</p>
                    )}
                </div>

                {/* Top Products */}
                <div className={styles.card}>
                    <h2>📦 Produk Paling Dilihat</h2>
                    {viewData?.topProducts && viewData.topProducts.length > 0 ? (
                        <div className={styles.list}>
                            {viewData.topProducts.slice(0, 10).map((p, i) => (
                                <div key={i} className={styles.listItem}>
                                    <span>{i + 1}. {p.product_name}</span>
                                    <span className={styles.count}>{p.view_count} views</span>
                                </div>
                            ))}
                        </div>
                    ) : (
                        <p className={styles.empty}>Belum ada data product views</p>
                    )}
                </div>
            </div>

            {/* Quiz Preferences */}
            <div className={styles.card}>
                <h2>🎯 Preferensi Quiz Pelanggan</h2>
                <div className={styles.prefsGrid}>
                    {Object.keys(prefLabels).map((key) => {
                        const data = searchData?.quizPreferences?.[key] || {};
                        const items = Object.entries(data).sort((a, b) => b[1] - a[1]);

                        if (items.length === 0) return null;

                        return (
                            <div key={key} className={styles.prefCard}>
                                <h4>{prefLabels[key]}</h4>
                                {items.slice(0, 3).map(([val, count]) => (
                                    <div key={val} className={styles.prefItem}>
                                        <span>{val}</span>
                                        <span className={styles.prefCount}>{count}</span>
                                    </div>
                                ))}
                            </div>
                        );
                    })}
                </div>
                {!searchData?.quizPreferences || Object.values(searchData.quizPreferences).every(v => Object.keys(v).length === 0) && (
                    <p className={styles.empty}>Belum ada data quiz preferences</p>
                )}
            </div>
        </div>
    );
}
